/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { jsPDF } from 'jspdf';

/**
 * Generates an off-screen canvas to draw the signature of Paola Ximena Arias Ruiz.
 */
function createSignatureImage(): string {
  const canvas = document.createElement('canvas');
  canvas.width = 720;
  canvas.height = 130;
  const ctx = canvas.getContext('2d');
  
  if (ctx) {
    // Clear background to white
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 720, 130);
    
    // Draw the signature replicating the uploaded path exactly
    ctx.strokeStyle = '#05070B'; // Rich charcoal black
    ctx.lineWidth = 2.8;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    // 1. Letter 'P'
    ctx.beginPath();
    ctx.moveTo(38, 30);
    ctx.lineTo(38, 105); // Vertical stem
    ctx.stroke();
    
    ctx.beginPath();
    // Huge loop curving to the left and wrapping over the top
    ctx.moveTo(38, 85);
    ctx.bezierCurveTo(22, 80, 22, 20, 38, 20);
    ctx.bezierCurveTo(55, 20, 68, 15, 72, 38);
    ctx.bezierCurveTo(74, 50, 58, 54, 38, 54);
    ctx.stroke();
    
    // 2. Letter 'a'
    ctx.beginPath();
    ctx.arc(92, 78, 16, 0, Math.PI * 2); // Oval body
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(108, 62);
    ctx.lineTo(108, 98); // Stem
    ctx.bezierCurveTo(108, 98, 110, 98, 112, 96); // Micro-tail
    ctx.stroke();
    
    // 3. Letter 'o'
    ctx.beginPath();
    ctx.ellipse(140, 78, 15, 16, 0, 0, Math.PI * 2);
    ctx.stroke();
    
    // 4. Letter 'l'
    ctx.beginPath();
    ctx.moveTo(170, 32);
    ctx.lineTo(170, 98);
    ctx.stroke();
    
    // 5. Letter 'a'
    ctx.beginPath();
    ctx.arc(198, 78, 16, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(214, 62);
    ctx.lineTo(214, 98);
    ctx.bezierCurveTo(214, 98, 216, 98, 218, 96);
    ctx.stroke();
    
    // 6. Letter 'X'
    ctx.beginPath();
    ctx.moveTo(242, 32);
    ctx.lineTo(268, 98);
    ctx.moveTo(268, 32);
    ctx.lineTo(242, 98);
    ctx.stroke();
    
    // 7. Letter 'i'
    ctx.beginPath();
    ctx.moveTo(286, 60);
    ctx.lineTo(286, 98);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(286, 45, 1.8, 0, Math.PI * 2); // Dot
    ctx.fillStyle = '#05070B';
    ctx.fill();
    
    // 8. Letter 'm'
    ctx.beginPath();
    ctx.moveTo(304, 60);
    ctx.lineTo(304, 98); // Left stem
    ctx.moveTo(304, 72);
    ctx.bezierCurveTo(304, 60, 316, 60, 324, 72);
    ctx.lineTo(324, 98); // Mid stem
    ctx.moveTo(324, 72);
    ctx.bezierCurveTo(324, 60, 336, 60, 344, 72);
    ctx.lineTo(344, 98); // Right stem
    ctx.stroke();
    
    // 9. Letter 'e'
    ctx.beginPath();
    ctx.moveTo(358, 78);
    ctx.lineTo(376, 78);
    ctx.bezierCurveTo(376, 58, 354, 58, 358, 78);
    ctx.bezierCurveTo(358, 94, 372, 96, 376, 92);
    ctx.stroke();
    
    // 10. Letter 'n'
    ctx.beginPath();
    ctx.moveTo(392, 60);
    ctx.lineTo(392, 98); // Left stem
    ctx.moveTo(392, 72);
    ctx.bezierCurveTo(392, 60, 404, 60, 412, 72);
    ctx.lineTo(412, 98); // Right stem
    ctx.stroke();
    
    // 11. Letter 'a'
    ctx.beginPath();
    ctx.arc(440, 78, 16, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(456, 62);
    ctx.lineTo(456, 98);
    ctx.bezierCurveTo(456, 98, 458, 98, 460, 96);
    ctx.stroke();
    
    // 12. Letter 'A'
    ctx.beginPath();
    ctx.moveTo(484, 98);
    ctx.lineTo(500, 32);
    ctx.lineTo(516, 98);
    ctx.moveTo(489, 72);
    ctx.lineTo(511, 72);
    ctx.stroke();
    
    // 13. Letter 'r'
    ctx.beginPath();
    ctx.moveTo(534, 60);
    ctx.lineTo(534, 98);
    ctx.moveTo(534, 74);
    ctx.bezierCurveTo(534, 64, 544, 62, 548, 64);
    ctx.stroke();
    
    // 14. Letter 'i'
    ctx.beginPath();
    ctx.moveTo(562, 60);
    ctx.lineTo(562, 98);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(562, 45, 1.8, 0, Math.PI * 2); // Dot
    ctx.fillStyle = '#05070B';
    ctx.fill();
    
    // 15. Letter 'a'
    ctx.beginPath();
    ctx.arc(588, 78, 16, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(604, 62);
    ctx.lineTo(604, 98);
    ctx.bezierCurveTo(604, 98, 606, 98, 608, 96);
    ctx.stroke();
    
    // 16. Letter 's'
    ctx.beginPath();
    ctx.moveTo(634, 64);
    ctx.bezierCurveTo(624, 60, 618, 72, 626, 78);
    ctx.bezierCurveTo(632, 82, 630, 94, 618, 98);
    ctx.stroke();
    
    // 17. Letter 'R'
    ctx.beginPath();
    ctx.moveTo(652, 32);
    ctx.lineTo(652, 98); // Stem
    ctx.stroke();
    
    ctx.beginPath();
    ctx.moveTo(652, 32);
    ctx.bezierCurveTo(672, 32, 672, 60, 652, 60); // Loop
    ctx.stroke();
    
    ctx.beginPath();
    ctx.moveTo(652, 60);
    ctx.lineTo(672, 98); // Leg
    ctx.stroke();
    
    // 18. Period '.'
    ctx.beginPath();
    ctx.arc(682, 96, 2, 0, Math.PI * 2);
    ctx.fillStyle = '#05070B';
    ctx.fill();
  }
  
  return canvas.toDataURL('image/png');
}

// Variables for layout budget
const margin = 20;
const pageWidth = 210;
const pageHeight = 297;
const contentWidth = pageWidth - 2 * margin; // 170mm

function checkPageBreak(doc: jsPDF, currentY: number, neededHeight: number): number {
  if (currentY + neededHeight > 275) {
    doc.addPage();
    drawPageFramework(doc, doc.getNumberOfPages());
    return 28; // standard Y start of any new page
  }
  return currentY;
}

function drawPageFramework(doc: jsPDF, pageNum: number) {
  if (pageNum <= 1) return; // avoid drawing header/footer on cover page
  
  // Header
  doc.setFillColor(7, 26, 54); // Azul Corporativo Principal (#071A36)
  doc.rect(20, 10, contentWidth, 5.5, 'F');
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  doc.setTextColor(255, 255, 255);
  doc.text('PHARMA TRACE v5.0 • INSUMEDIC S.A.S. ZOMAC (NIT 901201223)', 23, 13.5);
  
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 180, 162); // #00B4A2 Teal
  doc.text('SISTEMA INTEGRAL DE TRAZABILIDAD Y UNIDOSIS', 130, 13.5);
  
  // Footer divider line
  doc.setDrawColor(226, 232, 240); // slate-200
  doc.setLineWidth(0.25);
  doc.line(20, 282, 20 + contentWidth, 282);
  
  // Footer Text
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(100, 116, 139); // slate-500
  doc.text('PHARMA TRACE • Documento Confidencial de Control Oficial para INVIMA.', 20, 287);
  doc.text(`Página ${pageNum}`, 20 + contentWidth - 12, 287);
}

// Paragraph layout print with wrapping
function printParagraph(doc: jsPDF, text: string, state: { y: number }, fontSize = 10, isBold = false, color = [51, 65, 85], spacing = 4.8) {
  doc.setFont('helvetica', isBold ? 'bold' : 'normal');
  doc.setFontSize(fontSize);
  doc.setTextColor(color[0], color[1], color[2]);
  
  const lines = doc.splitTextToSize(text, contentWidth);
  for (const line of lines) {
    state.y = checkPageBreak(doc, state.y, spacing);
    doc.text(line, margin, state.y);
    state.y += spacing;
  }
  state.y += 2; // small tail margin
}

// Major H1 section break
function printH1(doc: jsPDF, title: string, state: { y: number }) {
  if (state.y > 180) { // start fresh page if low on space
    doc.addPage();
    drawPageFramework(doc, doc.getNumberOfPages());
    state.y = 28;
  } else {
    state.y += 7;
  }
  
  state.y = checkPageBreak(doc, state.y, 10);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(7, 26, 54); // Azul Corporativo Principal (#071A36)
  doc.text(title, margin, state.y);
  
  state.y += 2;
  doc.setDrawColor(28, 79, 138); // Azul Medio (#1C4F8A) underline accent
  doc.setLineWidth(0.5);
  doc.line(margin, state.y, margin + contentWidth, state.y);
  
  state.y += 6;
}

// Element H2 subtitle
function printH2(doc: jsPDF, title: string, state: { y: number }) {
  state.y = checkPageBreak(doc, state.y, 8);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(7, 26, 54); // Azul Corporativo Principal (#071A36)
  
  // Left vertical bar decorator
  doc.setFillColor(28, 79, 138); // Azul Medio (#1C4F8A)
  doc.rect(margin, state.y - 3.5, 2.5, 4.5, 'F');
  
  doc.text(title, margin + 4, state.y);
  state.y += 5.5;
}

// Callout highlight notes
function printNoteBlock(doc: jsPDF, title: string, text: string, state: { y: number }) {
  state.y = checkPageBreak(doc, state.y, 25);
  
  const splitText = doc.splitTextToSize(text, contentWidth - 10);
  const neededHeight = splitText.length * 4.5 + 11;
  
  state.y = checkPageBreak(doc, state.y, neededHeight);
  
  // Background fill
  doc.setFillColor(240, 253, 250); // teal-50
  doc.rect(margin, state.y - 4, contentWidth, neededHeight, 'F');
  
  // Vertical green bar
  doc.setFillColor(13, 148, 136); // teal-600
  doc.rect(margin, state.y - 4, 2.5, neededHeight, 'F');
  
  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(15, 23, 42);
  doc.text(title, margin + 5, state.y);
  
  state.y += 4.5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(51, 65, 85);
  
  for (const line of splitText) {
    doc.text(line, margin + 5, state.y);
    state.y += 4.5;
  }
  state.y += 3;
}

// Lists printer
function printBullet(doc: jsPDF, boldPrefix: string, desc: string, state: { y: number }) {
  state.y = checkPageBreak(doc, state.y, 6);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(15, 23, 42);
  doc.text('•', margin + 3, state.y);
  
  const prefixSize = doc.getTextWidth(boldPrefix + ': ');
  doc.text(boldPrefix + ': ', margin + 8, state.y);
  
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(51, 65, 85);
  const totalOffset = margin + 8 + prefixSize;
  const splitDesc = doc.splitTextToSize(desc, contentWidth - 12 - prefixSize);
  
  let isFirstLine = true;
  for (const line of splitDesc) {
    state.y = checkPageBreak(doc, state.y, 4.3);
    doc.text(line, isFirstLine ? totalOffset : margin + 8, state.y);
    state.y += 4.3;
    isFirstLine = false;
  }
  state.y += 1;
}

// Generalized highly robust table rendering function to solve overlaps and support column wrapping
function printTable(
  doc: jsPDF,
  headers: string[],
  colWidths: number[],
  rows: string[][],
  state: { y: number },
  headerColor = [15, 23, 42]
) {
  state.y = checkPageBreak(doc, state.y, 16);
  
  const xCoords: number[] = [];
  let currentX = margin;
  for (const w of colWidths) {
    xCoords.push(currentX);
    currentX += w;
  }
  
  // Draw header row
  const headerHeight = 7.5;
  doc.setFillColor(headerColor[0], headerColor[1], headerColor[2]);
  doc.rect(margin, state.y, contentWidth, headerHeight, 'F');
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(255, 255, 255);
  for (let i = 0; i < headers.length; i++) {
    doc.text(headers[i], xCoords[i] + 2, state.y + 4.8);
  }
  
  state.y += headerHeight;
  
  // Draw body rows
  for (const row of rows) {
    // Wrap text for all cells to find max row height
    let maxLines = 1;
    const splitCells: string[][] = [];
    for (let i = 0; i < row.length; i++) {
      const cellText = row[i];
      const maxColW = colWidths[i] - 3;
      const split = doc.splitTextToSize(cellText, maxColW);
      splitCells.push(split);
      if (split.length > maxLines) {
        maxLines = split.length;
      }
    }
    
    const rowHeight = maxLines * 4.2 + 2.5;
    
    // Check page break for this row
    const prevY = state.y;
    state.y = checkPageBreak(doc, state.y, rowHeight);
    
    if (state.y < prevY) {
      // Redraw header row on the new page
      doc.setFillColor(headerColor[0], headerColor[1], headerColor[2]);
      doc.rect(margin, state.y, contentWidth, headerHeight, 'F');
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.setTextColor(255, 255, 255);
      for (let i = 0; i < headers.length; i++) {
        doc.text(headers[i], xCoords[i] + 2, state.y + 4.8);
      }
      state.y += headerHeight;
    }
    
    // Draw row background (white)
    doc.setFillColor(255, 255, 255);
    doc.rect(margin, state.y, contentWidth, rowHeight, 'F');
    
    // Draw row outer rectangle
    doc.setDrawColor(203, 213, 225); // slate-300
    doc.setLineWidth(0.2);
    doc.rect(margin, state.y, contentWidth, rowHeight, 'S');
    
    // Draw inner vertical borders
    for (let i = 1; i < colWidths.length; i++) {
      doc.line(xCoords[i], state.y, xCoords[i], state.y + rowHeight);
    }
    
    // Render cells text with specific style adjustments
    for (let i = 0; i < row.length; i++) {
      const cellLines = splitCells[i];
      
      // Select appropriate color and font weights for visual cues
      if (row[i].includes('RESTRICCION') || row[i].includes('NO ACCESIBLE')) {
        doc.setTextColor(220, 38, 38); // Red-600
        doc.setFont('helvetica', 'bold');
      } else if (
        row[i].includes('SI -') || 
        row[i].includes('ACCESO') || 
        row[i] === 'SI' || 
        row[i].includes('AUTOMATICA') || 
        row[i].includes('ACTIVO')
      ) {
        doc.setTextColor(13, 148, 136); // Teal-600
        doc.setFont('helvetica', 'bold');
      } else {
        doc.setTextColor(51, 65, 85); // Slate-600
        doc.setFont('helvetica', 'normal');
      }
      
      doc.setFontSize(7);
      let textY = state.y + 4;
      for (const line of cellLines) {
        doc.text(line, xCoords[i] + 2, textY);
        textY += 4.2;
      }
    }
    
    state.y += rowHeight;
  }
  
  state.y += 3; // buffer spacing
}

// Audit Logs Table using generalized generator
function printAuditTable(doc: jsPDF, state: { y: number }) {
  const headers = ['Fecha/Hora', 'Cargo / Rol de Acceso', 'Modulo Técnico', 'Acción', 'Descripción del Cambio'];
  const colWidths = [26, 38, 22, 26, 58]; // Sums to 170mm (contentWidth)
  const rows = [
    ['2026-05-25 15:30', 'Director Técnico y de Calidad', 'Disposición', 'Crear Liberación', 'Lote Liberado con Dictamen de calidad optimo para Consecutivo OP-45'],
    ['2026-05-25 16:15', 'Administrador de Sucursal', 'Seguridad', 'Creación Colaborador', 'Se creo el perfil clínico para el auxiliar técnico de dispensación'],
    ['2026-05-25 18:00', 'Auxiliar Técnico / Operario', 'Órdenes', 'Registro Órdenes', 'Ingreso de materia prima ampolletas de Solución Inyectable bulk'],
    ['2026-05-26 12:45', 'Administrador de Sucursal', 'Backups', 'Copia Seguridad', 'Copia de seguridad manual e inalterable generada con éxito en la nube']
  ];
  
  printTable(doc, headers, colWidths, rows, state);
}

// Module Permisology Table using generalized generator
function printPermisologiaTable(doc: jsPDF, state: { y: number }) {
  const headers = ['Módulo Técnico', 'Director Técnico Calidad', 'Auxiliar Reempaque / Operario', 'Regente Farmacéutico', 'Administrador Sistemas'];
  const colWidths = [36, 33, 33, 33, 35]; // Sums to 170mm (contentWidth)
  const rows = [
    ['Registro de Órdenes (Reempaque)', 'SI - LECTURA / BUSCADOR', 'SI - CREA / EDITA / BUSCA', 'SI - LECTURA / BUSCADOR', 'SI - CONTROL Y AUDITORÍA'],
    ['Disposición de Lotes y Liberación', 'SI - EXCLUSIVO CON FIRMA', 'NO ACCESIBLE / RESTRICCION', 'SI - LECTURA ACTAS', 'SI - LECTURA Y BÚSQUEDA'],
    ['Panel de Productividad (D3/Recharts)', 'SI - CONSULTA ANÁLISIS', 'NO ACCESIBLE / RESTRICCION', 'SI - DETALLES SEDE', 'SI - AUDITORÍA GENERAL'],
    ['Gestión de Equipo y Firmas', 'NO ACCESIBLE / RESTRICCION', 'NO ACCESIBLE / RESTRICCION', 'NO ACCESIBLE / RESTRICCION', 'SI - ADMINISTRACIÓN COMPLETA'],
    ['Gestor Resguardo y Reset Total', 'SI - AUTORIZADO Y AUDITORÍA', 'NO ACCESIBLE / RESTRICCION', 'NO ACCESIBLE / RESTRICCION', 'SI - EJECUCIÓN Y RESET CONTROLADO'],
    ['App Móvil (PWA / APK Android iOS)', 'SI - ACCESO COMPLETO', 'SI - ACCESO OPERATIVO', 'SI - ACCESO CONSULTA', 'SI - ACCESO ADMINISTRATIVO']
  ];
  
  printTable(doc, headers, colWidths, rows, state, [15, 23, 42]);
}

/**
 * Main export function - Creates a beautiful PDF manual
 */
export function downloadInsumedicManualPdf() {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  // ==========================================
  // PAGE 1: COVER PAGE
  // ==========================================
  
  // Top thick corporate header band in deep navy (#071A36)
  doc.setFillColor(7, 26, 54);
  doc.rect(0, 0, 210, 48, 'F');
  
  // Teal accent line
  doc.setFillColor(0, 180, 162); // #00B4A2
  doc.rect(0, 48, 210, 3, 'F');
  
  // Draw PHARMA TRACE Logo Emblem Capsule
  const logoX = 20;
  const logoY = 16;
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(logoX, logoY, 48, 12, 6, 6, 'F');
  doc.setFillColor(0, 180, 162);
  doc.roundedRect(logoX + 24, logoY, 24, 12, 6, 6, 'F');
  doc.rect(logoX + 24, logoY, 5, 12, 'F');
  // Cross
  doc.setFillColor(7, 26, 54);
  doc.rect(logoX + 22.5, logoY + 3, 3, 6, 'F');
  doc.rect(logoX + 21, logoY + 4.5, 6, 3, 'F');
  // Text inside logo
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(7, 26, 54);
  doc.text('PHARMA', logoX + 5, logoY + 8);
  doc.setTextColor(255, 255, 255);
  doc.text('TRACE', logoX + 30, logoY + 8);

  // Top Header Titles
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(255, 255, 255);
  doc.text('SOFTWARE PHARMA TRACE v5.0', 74, 25);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(203, 213, 225); // slate-300
  doc.text('Licenciado para: COMERCIALIZADORA INSUMEDIC S.A.S. ZOMAC', 74, 33);
  doc.text('Desarrollado por: Quality Procesos y Servicios S.A.S.', 74, 39);

  // Vertical title card in the middle of page
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(7, 26, 54);
  doc.text('MANUAL TÉCNICO Y DE USUARIO', 20, 88);
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(28, 79, 138); // #1C4F8A
  doc.text('SISTEMA INTEGRAL PARA CONTROL, TRAZABILIDAD Y GESTIÓN DE UNIDOSIS', 20, 96);
  
  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 116, 139); // slate-500
  doc.text('Acondicionamiento Farmacéutico, Fraccionamiento, Liberación de Calidad y Auditoría INVIMA', 20, 103);

  // Central solid dark grey decorative line
  doc.setDrawColor(0, 180, 162);
  doc.setLineWidth(1.2);
  doc.line(20, 110, 190, 110);
  
  // Document Metadata Table / List on Cover page
  doc.setFillColor(248, 250, 252); // slate-50
  doc.rect(20, 128, contentWidth, 122, 'F');
  doc.setDrawColor(226, 232, 240); // slate-200
  doc.setLineWidth(0.4);
  doc.rect(20, 128, contentWidth, 122, 'S');
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(7, 26, 54);
  doc.text('FICHA TÉCNICA DEL SOFTWARE Y LICENCIA OPERATIVA:', 25, 138);
  
  const nowManual = new Date();
  const optionsManual: Intl.DateTimeFormatOptions = { month: 'long', year: 'numeric' };
  const dateFormattedManualRaw = nowManual.toLocaleDateString('es-CO', optionsManual);
  const dateFormattedManual = dateFormattedManualRaw.charAt(0).toUpperCase() + dateFormattedManualRaw.slice(1);

  const metadata = [
    ['Nombre del Software:', 'PHARMA TRACE (Control, Trazabilidad y Unidosis v5.0)'],
    ['Empresa Licenciataria:', 'Comercializadora Insumedic S.A.S. Zomac (NIT: 901201223)'],
    ['Desarrollador Oficial:', 'Quality Procesos y Servicios S.A.S. (NIT: 9016276389)'],
    ['Sede Operativa Principal:', 'Neiva, Huila, Colombia (Laboratorio de Reempaque)'],
    ['Cumplimiento Normativo:', 'Decreto 2200 de 2005 / Res. 1403 de 2007 (INVIMA)'],
    ['Arquitectura de Software:', 'Full-Stack React + Cloud Firestore + WebAPK / PWA'],
    ['Nivel de Confidencialidad:', 'Clasificado Interno - Documento Controlado de Calidad'],
    ['Novedades Versión 5.0:', 'Menú Recursos, Branding Multi-tenant, Sincronizador Cloud'],
    ['Fecha de Emisión:', dateFormattedManual],
    ['Estado de Certificación:', 'Certificado de Funcionalidad Técnica 100% Aprobado']
  ];
  
  let metaY = 146;
  doc.setFontSize(8.5);
  for (const item of metadata) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(71, 85, 105);
    doc.text(item[0], 25, metaY);
    
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(15, 23, 42);
    doc.text(item[1], 72, metaY);
    
    metaY += 8.2;
  }
  
  // Bottom signature block on cover page representing authority
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(148, 163, 184);
  doc.text('© 2026 Quality Procesos y Servicios S.A.S. • PHARMA TRACE v5.0 para Insumedic S.A.S. Zomac.', 20, 280);
  
  // ==========================================
  // PAGE 2: CONTENIDO CENTRAL
  // ==========================================
  doc.addPage();
  let state = { y: 28 };
  drawPageFramework(doc, 2);
  
  printH1(doc, '1. Introducción y Enfoque Regulatorio', state);
  printParagraph(doc, 'El presente manual técnico y de usuario establece las directrices operativas de la plataforma de software PHARMA TRACE v5.0 ("Sistema Integral para Control, Trazabilidad y Gestión de Unidosis"), diseñada e implementada por Quality Procesos y Servicios S.A.S. para Comercializadora Insumedic S.A.S. Zomac (NIT 901201223) en su sede operativa de Neiva, Huila, Colombia.', state);
  printParagraph(doc, 'PHARMA TRACE sistematiza los procesos de reempaque y fraccionamiento de medicamentos sólidos, líquidos y semisólidos. La herramienta automatiza el cálculo de rendimientos y mermas físicas, erradicando transcripciones manuales e integrando un esquema de firma electrónica para la liberación de lotes clínicos exigida por las autoridades sanitarias.', state);
  printParagraph(doc, 'El desarrollo cumple de forma rigurosa con el Decreto 2200 de 2005 y la Resolución 1403 de 2007 del Ministerio de Salud y Protección Social de Colombia, garantizando que el INVIMA y las Secretarías de Salud dispongan de auditorías con trazabilidad completa de lotes.', state);
  
  printNoteBlock(doc, 'Arquitectura de Marca Institucional & Multi-Tenant:', 'PHARMA TRACE incorpora una banda institucional corporativa que distingue de forma clara la marca del software (PHARMA TRACE) de la entidad usuaria suscriptora (Insumedic S.A.S. Zomac u otras organizaciones). Cada cliente mantiene su logotipo corporativo, colores institucionalizados y portal de acceso personalizado.', state);
  
  printH1(doc, '2. Arquitectura de Módulos de la Plataforma', state);
  printParagraph(doc, 'La solución PHARMA TRACE v5.0 se compone de los siguientes módulos funcionales e interactivos:', state);
  
  printH2(doc, 'A) Módulo de Registro de Órdenes de Producción', state);
  printBullet(doc, 'Consecutivo e Ingreso Seguro', 'El operario ingresa medicamentos en bulk provenientes de compras validadas, asignando un Consecutivo de Ingreso Único.', state);
  printBullet(doc, 'Rendimiento y Merma en Tiempo Real', 'La plataforma calcula de forma automatizada la pérdida de medicamento (mermas físicas) contrastando las cantidades iniciales teóricas contra el conteo final real.', state);
  printBullet(doc, 'Emisión Estricta de PDF', 'Descarga instantánea de la orden formateada con márgenes limpios y grillas divisorias nítidas para firmas de puño y letra del operario de reempaque.', state);

  // ==========================================
  // PAGE 3: CONTINUACIÓN DE ARQUITECTURA
  // ==========================================
  doc.addPage();
  state.y = 28;
  drawPageFramework(doc, 3);
  
  printH2(doc, 'B) Módulo de Disposición y Liberación de Calidad', state);
  printParagraph(doc, 'Corresponde a la interfaz restringida de control de calidad, donde solo los usuarios autorizados (Director Técnico / Quality) pueden inspeccionar digitalmente las mermas generadas y emitir el dictamen definitivo del lote:', state);
  printBullet(doc, 'Estatus de Aprobación (Liberado)', 'El lote cumple el 100% de controles microbiológicos y físicos. Se expide el Certificado Oficial de Liberación Técnica.', state);
  printBullet(doc, 'Estatus de Rechazo', 'Se bloquea permanentemente la distribución del lote clínico ante desviaciones observadas en el proceso de frfraccionamiento.', state);
  printBullet(doc, 'Estatus de Cuarentena', 'Envía de manera inmediata el lote a resguardo virtual restringido hasta que se resuelva la investigación de desviaciones.', state);
  
  printH2(doc, 'C) Módulo Analítico e Indicadores (Recharts / D3)', state);
  printParagraph(doc, 'Ofrece visualizaciones directas orientadas a la toma de decisiones gerenciales. Permite auditar el total de tabletas acondicionadas por auxiliar de reempaque, la eficiencia comparativa inter-sede y el índice acumulativo de desviaciones por periodo temporal.', state);
  
  printH2(doc, 'D) Módulo de Gestión de Equipo y Firmas Digitales', state);
  printParagraph(doc, 'Directorio central de talento humano asistencial. Permite registrar auxiliares y directores, almacenar su imagen de firma oficial para inserción electrónica automática, configurar su cédula y regular por separado su perfil específico de acceso técnico de seguridad.', state);
  
  printH2(doc, 'E) Centro de Notificaciones Corporativo (Workspace Gmail)', state);
  printParagraph(doc, 'Garantiza la comunicación automatizada de reportes regulatorios. El Director de Calidad puede disparar correos transaccionales adjuntando certificados y reportes a entidades prestadoras de servicios de salud de manera ágil.', state);

  // ==========================================
  // PAGE 4: SEGURIDAD DE LA INFORMACIÓN
  // ==========================================
  doc.addPage();
  state.y = 28;
  drawPageFramework(doc, 4);
  
  printH1(doc, '3. Seguridad y Custodia de la Información', state);
  printParagraph(doc, 'Por tratarse de un sistema con impacto directo en la salud pública y manipulación de datos sensibles, el aplicativo contempla directrices extremas de seguridad digital alineadas a la Ley de Protección de Datos de Colombia:', state);
  
  printBullet(doc, 'Cifrado de Claves SHA-256', 'Las claves de acceso de los colaboradores de la sede Neiva se someten a hash matemático de sentido único irreversible en almacenamiento.', state);
  printBullet(doc, 'Aislamiento de Entorno Multi-Inquilino', 'Cada base de datos asocia un identificador unívoco corporativo. Los datos de Comercializadora Insumedic S.A.S. Zomac jamás se mezclarán con otras organizaciones lógicas de la nube.', state);
  printBullet(doc, 'Ocultamiento de Secrets en Servidor', 'Los tokens que operan integraciones de correo y los servicios de base de datos e inteligencia artificial residen estrictamente en variables de entorno del servidor. Nunca se exponen al navegador.', state);
  
  printH2(doc, 'Historial Inalterable de Auditoría (Logs del Sistema)', state);
  printParagraph(doc, 'Cada evento operativo se documenta en un log con registro inalterable. Este módulo de solo-lectura es el principal respaldo probatorio en visitas inspectivas del INVIMA:', state);
  
  printAuditTable(doc, state);

  // ==========================================
  // PAGE 5: SEGMENTACIÓN DE PERMISOS
  // ==========================================
  doc.addPage();
  state.y = 28;
  drawPageFramework(doc, 5);
  
  printH1(doc, '4. Segmentación de Permisos por Perfiles', state);
  printParagraph(doc, 'El sistema impide por diseño técnico la autoliberación y el cruce de responsabilidades críticas mediante una estricta jerarquía de seguridad que detalla con claridad las facultades y restricciones de cada rol operativo en Comercializadora Insumedic:', state);
  
  printBullet(doc, 'Director Técnico y de Calidad - PERMISOS', 'Dictamen y validación oficial de lotes de medicamentos acondicionados. Emisión de actas firmadas digitalmente con hash seguro de validación. Monitoreo de indicadores analíticos.', state);
  printBullet(doc, 'Director Técnico y de Calidad - RESTRICCIONES', 'Imposibilidad de registrar u omitir desviaciones de forma directa sobre las órdenes de producción iniciales o alterar la retícula documental de control.', state);
  
  printBullet(doc, 'Auxiliar de Reempaque / Operario Logístico - PERMISOS', 'Creación y corrección de órdenes de fraccionamiento, pesaje inicial, toma de temperatura física, registro de mermas directas y cálculo automático de rendimiento de unidosis.', state);
  printBullet(doc, 'Auxiliar de Reempaque / Operario Logístico - RESTRICCIONES', 'Bloqueo completo al dictamen clínico final de lotes (Liberación, Rechazo, Cuarentena) y firma electrónica de liberaciones. Sin atribuciones de administración de personal.', state);
  
  printBullet(doc, 'Regente Farmacéutico - PERMISOS', 'Supervisión de prácticas de piso y acondicionado clínico en Neiva. Consulta de actas de liberación técnica e impresión de reportes regulatorios autorizados.', state);
  printBullet(doc, 'Regente Farmacéutico - RESTRICCIONES', 'No tiene la facultad de asentar firmas de liberación con certificado oficial de calidad salvo delegación de cargo debidamente parametrizada en el sistema.', state);
  
  printBullet(doc, 'Administrador del Sistema - PERMISOS', 'Control global del talento clínico e ingresos de la sucursal. Habilitación / deshabilitación de módulos con casillas de validación ágil, restablecer claves de acceso y gestión del Módulo de Backups (Copias de Seguridad).', state);
  printBullet(doc, 'Administrador del Sistema - RESTRICCIONES', 'Impedido de firmar técnicamente los lotes clínicos o liberar sustancias controladas en el panel médico de toma de decisiones.', state);
  
  printH2(doc, 'Matriz de Permisología y Restricciones Técnicas:', state);
  printParagraph(doc, 'Tabla integradora que regula de forma estricta los privilegios de los usuarios respecto a cada módulo operativo del aplicativo:', state);
  
  printPermisologiaTable(doc, state);

  // ==========================================
  // PAGE 6: PERSISTENCIA Y MEJORAS RECIENTES
  // ==========================================
  doc.addPage();
  state.y = 28;
  drawPageFramework(doc, 6);
  
  printH1(doc, '5. Persistencia, Custodia y Copias de Seguridad', state);
  printParagraph(doc, 'El sistema cuenta con un motor distribuido de altísima disponibilidad técnica en tiempo real y protocolos de protección que aseguran la operación en la sede de Neiva:', state);
  
  printBullet(doc, 'Sincronización vía Google Firestore', 'Base de datos NoSQL alojada en la nube con réplica multizona automática. Permite que múltiples operarios en la sede de Neiva actualicen inventarios clínicos simultáneamente sin colisiones de datos.', state);
  printBullet(doc, 'Política de Caché Web Offline', 'En caso de caídas transitorias de internet en el laboratorio, la aplicación almacena localmente las acciones del usuario. Al recuperarse la conectividad, realiza un volcado transaccional inalterado a la nube.', state);
  printBullet(doc, 'Copias de Seguridad (Backups) del Sistema', 'Para mitigar pérdidas por fallas operativas o alteración accidental, el sistema integra un Gestor de Resguardos que permite crear respaldos completos de la base de datos de manera manual y programada.', state);
  printBullet(doc, 'Ejecución Centinela programada a las 18:00', 'De forma pasiva e inteligente, el sistema ejecuta una copia de seguridad automática todos los días a las 18:00 horas local, garantizando un punto de restauración confiable e inalterable en base de datos.', state);
  
  printH1(doc, '6. Mejoras Tecnológicas y Novedades de la Versión 5.0', state);
  printParagraph(doc, 'Con el objetivo de proveer una plataforma de nivel corporativo industrial, altamente disponible, accesible y ergonómica, se implementaron mejoras exclusivas sobre la plataforma de Insumedic S.A.S. Zomac:', state);
  printBullet(doc, 'Reorganización del Encabezado - Menú Recursos & Utilidades', 'Se unificaron las herramientas de gestión superior (Sincronizador Nube en Tiempo Real, Instalador de App Celular APK/PWA, Certificado de Funcionalidad PDF y Manual Técnico de Usuario) en un único menú desplegable intuitivo tipo popover ("Recursos & Utilidades"), descongestionando la barra principal sin restringir el acceso a ningún usuario.', state);
  printBullet(doc, 'Banda Institucional y Marca de Producto (PHARMA TRACE)', 'Se incorporó una banda superior corporativa (#071A36) con gradiente azul medio (#1C4F8A) que establece la marca oficial del producto "PHARMA TRACE" diferenciándola con elegancia del logotipo y nombre del cliente suscriptor (Insumedic S.A.S. Zomac).', state);
  printBullet(doc, 'Modo Consulta Activo (Read-Only Mode)', 'Ante el vencimiento de la fecha de suscripción o licencia corporativa, el sistema habilita automáticamente el Modo Consulta. Los usuarios mantienen acceso irrestricto a la visualización, filtrado, búsqueda y descarga de reportes e historia de lotes en PDF, mientras se bloquean de forma segura las acciones de creación, edición o eliminación hasta la renovación de la licencia.', state);
  printBullet(doc, 'Interruptor de Tema - Modo Oscuro y Modo Claro', 'Incorpora un alternador visual de alta velocidad situado en la barra superior (Header) que permite conmutar entre Modo Claro y Modo Oscuro. Cumple con estándares de accesibilidad WCAG AA, adaptando paletas de color, tablas de órdenes, gráficos de productividad e insumos visuales para reducir la fatiga ocular.', state);
  printBullet(doc, 'Adaptación Móvil e Instalación por APK (Android y Apple iOS)', 'La plataforma cuenta con soporte PWA (Progressive Web App) y WebAPK con Service Worker offline y manifest nativo. Permite instalar la aplicación directamente en dispositivos móviles mediante un modal interactivo que guía paso a paso al usuario en Google Chrome para Android o Safari para iOS.', state);
  printBullet(doc, 'Módulo Gestor de Resguardo y Seguridad del Sistema', 'Permite a los usuarios autorizados (Director Técnico y de Calidad, Administrador del Sistema y JUAN.VALENCIA) consultar la lista cronológica de copias de seguridad, ejecutar resguardos manuales instantáneos y programar backups automáticos diarios tipo centinela a las 18:00 hrs.', state);
  printBullet(doc, 'Botón de Reestablecimiento Total del Sistema (Reset de Fábrica)', 'Incorpora un mecanismo protegido con doble factor de seguridad (palabra de confirmación "REESTABLECER" + contraseña de usuario) que permite limpiar de forma controlada las órdenes de producción, el historial de lotes y liberaciones, reiniciando el consecutivo oficial a N° 1 para nuevos periodos operativos.', state);
  printBullet(doc, 'Buscadores Rápidos e Inteligentes en Tiempo Real', 'Se incluyeron buscadores de filtrado instantáneo en el Módulo de Registro de Órdenes y en el Historial de Disposiciones & Liberaciones. Permiten localizar registros al instante por Número de Orden (OP), Lote RP, Medicamento, Código y Nombre de Inspector.', state);
  printBullet(doc, 'Soporte Documental con Retícula de Control de Calidad', 'Las órdenes de producción, certificados y manuales generados en PDF disponen del isotipo oficial de PHARMA TRACE y de una grilla de líneas divisorias de alta nitidez que facilita la revisión visual e inspección reglamentaria por parte de auditores autorizados del INVIMA.', state);

  // ==========================================
  // PAGE 7: RESPONSABILIDAD LEGAL Y FIRMAS
  // ==========================================
  doc.addPage();
  state.y = 28;
  drawPageFramework(doc, 7);
  
  printH1(doc, '7. Compromiso Corporativo y Firma de Entrega', state);
  printParagraph(doc, 'Este manual representa el pilar documental y de instrucción oficial para el funcionamiento óptimo del aplicativo implementado para Comercializadora Insumedic S.A.S. Zomac. Cada procedimiento técnico registrado se considera veraz, inalterable y vinculante para propósitos legales e INVIMA.', state);
  
  printParagraph(doc, 'La veracidad y custodia de los accesos brindados recae sobre el Representante Legal y los administradores específicos de cada cuenta corporativa de empresa.', state);
  
  // Signature Area
  state.y += 12;
  state.y = checkPageBreak(doc, state.y, 80);
  
  // Signature Title Label
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(15, 23, 42);
  doc.text('DOCUMENTO REVISADO Y AVALADO PARA CERTIFICACIÓN CONTINUA:', margin, state.y);
  
  state.y += 8;
  
  // Embed programmatic canvas-rendered signature
  const signatureBase64 = createSignatureImage();
  try {
    // Width and height of the signature image on the PDF: 60mm x 45mm
    doc.addImage(signatureBase64, 'PNG', margin, state.y, 60, 45);
  } catch (err) {
    console.error('Error rendering programmatic signature on PDF:', err);
  }
  
  // Push down under signature image
  state.y += 46;
  
  // Decorative lines and texts for signature info
  doc.setDrawColor(71, 85, 105); // slate-600 line
  doc.setLineWidth(0.4);
  doc.line(margin, state.y, margin + 85, state.y);
  
  state.y += 5;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.text('Paola Ximena Arias Ruiz', margin, state.y);
  
  state.y += 4.5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(71, 85, 105);
  doc.text('Representante Legal', margin, state.y);
  
  state.y += 4;
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(13, 148, 136); // teal-600 Highlight for the QA process house
  doc.text('Quality Procesos y Servicios S.A.S.', margin, state.y);
  
  state.y += 4;
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 116, 139);
  doc.text('NIT: 9016276389', margin, state.y);
  
  state.y += 12;
  printNoteBlock(doc, 'Certificación de Documento Controlado:', 'Este manual se entrega digitalmente firmado por la gerencia y se encuentra catalogado como documento controlado de Quality en asocio con Comercializadora Insumedic S.A.S. Zomac.', state);
  
  // Save PDF trigger
  doc.save('Manual_Completo_Aplicativo_Insumedic_901201223.pdf');
}
